import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import { useDebounce } from '../hooks/useDebounce';
import logger from '../utils/logger';
import env from '../config/env';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import Select from 'react-select';
import { ArrowLeftCircle, RefreshCcw, Trash2, PlusCircle } from 'lucide-react';

function KthimProdukti() {
  const { lokacion_id, roli } = useAuth();
  const { theme } = useTheme();
  const { getCurrencyCode } = useSettings();
  const currencyCode = getCurrencyCode();
  const [kuponKodi, setKuponKodi] = useState('');
  const [shitje, setShitje] = useState(null);
  const [lokacioniShitjes, setLokacioniShitjes] = useState(null);

  // Fetch produktet and lokacionet
  const { data: produktetData } = useApi(
    () => apiService.get('/produkte'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: lokacionetData } = useApi(
    () => apiService.get('/lokacionet'),
    [],
    { immediate: roli === 'admin', showNotifications: false }
  );

  const produktet = Array.isArray(produktetData) ? produktetData : (produktetData?.data || []);
  const lokacionet = Array.isArray(lokacionetData) ? lokacionetData : (lokacionetData?.data || []);

  const [item, setItem] = useState({
    produkt_id: '',
    madhesi_id: '',
    ngjyra_id: '',
    sasia: 1,
    opsione: [],
    cmimi: 0,
    cmimi_origjinal: 0,
    zbritje: 0,
    foto: null
  });

 const effectiveLokacionId = useMemo(() => {
  if (roli === 'admin') {
    return lokacioniShitjes || '';
  }
  return lokacion_id;
}, [roli, lokacioniShitjes, lokacion_id]);



  const handleChange = async (field, value) => {
    const updated = { ...item, [field]: value };

    if (field === 'produkt_id') {
      if (!value) {
        setItem({ produkt_id: '', madhesi_id: '', ngjyra_id: '', sasia: 1, opsione: [], cmimi: 0, cmimi_origjinal: 0, zbritje: 0, foto: null });
        return;
      }
 
      try {
        const res = await apiService.get(`/produkte/${value}/detaje-lokacion`, {
          params: { lokacion_id: effectiveLokacionId }
        });


        const produkt = res;
        updated.opsione = produkt?.variacione?.filter(s => s.sasia > 0) || [];
        updated.madhesi_id = '';
        updated.ngjyra_id = '';
        updated.cmimi = produkt?.cmimi_shitjes || 0;
        updated.cmimi_origjinal = produkt?.cmimi_shitjes || 0;
        updated.zbritje = produkt?.zbritje_perqindje || 0;
        if (updated.zbritje > 0) {
          updated.cmimi = +(updated.cmimi_origjinal - (updated.cmimi_origjinal * updated.zbritje / 100)).toFixed(2);
        }
        updated.foto = produkt?.foto?.[0]?.file_path || null;
      } catch (error) {
        console.error("Gabim gjatë marrjes së produktit:", error);
        return;
      }
    }

    if (field === 'madhesi_id') {
      updated.ngjyra_id = '';
    }

    if (field === 'cmimi') {
      updated.cmimi = parseFloat(value) || 0;
    }

    setItem(updated);
  };

  const madhesiUnike = Array.from(
    new Map(item.opsione.map(o => [o.madhesi_id, { id: o.madhesi_id, emri: o.madhesia }])).values()
  );

  const ngjyraUnike = Array.from(
    new Map(
      item.opsione
        .filter(o => o.madhesi_id === item.madhesi_id)
        .map(o => [o.ngjyra_id, { id: o.ngjyra_id, emri: o.ngjyra }])
    ).values()
  );

  const getMaxSasi = () => {
    const o = item.opsione.find(
      x => x.madhesi_id === item.madhesi_id && x.ngjyra_id === item.ngjyra_id
    );
    return o?.sasia || 0;
  };

  const handleAdd = () => {
    if (!item.produkt_id || !item.madhesi_id || !item.ngjyra_id || !item.sasia) return;

    const ekzistues = shitje?.detaje?.find(x =>
      x.produkt_id === item.produkt_id &&
      x.madhesi_id === item.madhesi_id &&
      x.ngjyra_id === item.ngjyra_id
    );

    let updatedShitje = { ...shitje };

    if (ekzistues) {
      ekzistues.sasia += parseInt(item.sasia);
    } else {
      updatedShitje.detaje = [
  ...(updatedShitje.detaje || []),
  {
    produkt_id: item.produkt_id,
    madhesi_id: item.madhesi_id,
    ngjyra_id: item.ngjyra_id,
    sasia: item.sasia,
    cmimi_shitjes: item.cmimi,
    // për afishim në frontend
    produkt: produktet.find(p => p.id === parseInt(item.produkt_id)),
    madhesi: { id: item.madhesi_id, emri: madhesiUnike.find(m => m.id === item.madhesi_id)?.emri || '' },
    ngjyra: { id: item.ngjyra_id, emri: ngjyraUnike.find(n => n.id === item.ngjyra_id)?.emri || '' }
  }
];

    }

    updatedShitje.totali = updatedShitje.detaje.reduce((acc, d) => acc + d.cmimi_shitjes * d.sasia, 0);

    setShitje(updatedShitje);
    setItem({ produkt_id: '', madhesi_id: '', ngjyra_id: '', sasia: 1, opsione: [], cmimi: 0, cmimi_origjinal: 0, zbritje: 0, foto: null });
  };

  const handleRemove = (idx) => {
    const updated = { ...shitje };
    updated.detaje.splice(idx, 1);
    updated.totali = updated.detaje.reduce((acc, d) => acc + d.cmimi_shitjes * d.sasia, 0);
    setShitje(updated);
  };

  const { execute: kthimProdukti, loading: kthimLoading } = useMutation(
    async (data) => {
      return await apiService.post('/kthim-produkti', data);
    },
    {
      successMessage: 'Operacioni u krye me sukses!'
    }
  );

  const handleRikthim = async () => {
    try {
      await kthimProdukti({
        kupon_kodi: kuponKodi,
        veprimi: 'kthim'
      });
      setShitje(null);
      setKuponKodi('');
    } catch (err) {
      // Error handled by useMutation
    }
  };

  const handleNdryshim = async () => {
    try {
      await kthimProdukti({
        kupon_kodi: kuponKodi,
        veprimi: 'ndryshim',
        detajet: (shitje?.detaje ?? []).map((d) => ({
          artikull_id: d.id,
          produkt_id: d.produkt.id,
          madhesi_id: d.madhesi.id,
          ngjyra_id: d.ngjyra.id,
          sasia: d.sasia,
          cmimi_shitjes: d.cmimi_shitjes || 0
        }))
      });
      setShitje(null);
      setKuponKodi('');
    } catch (err) {
      // Error handled by useMutation
    }
  };

  const handleKerkoKupon = async () => {
    if (!kuponKodi.trim()) {
      return;
    }
    try {
      const res = await apiService.get(`/kthim-produkti/${kuponKodi}`);
      if (res) {
        setShitje(res);
        if (res.lokacion_id) {
          setLokacioniShitjes(res.lokacion_id);
        }
      }
    } catch (err) {
      logger.error('Gabim gjatë kërkimit të kuponit:', err);
      setShitje(null);
    }
  };

  // Custom styles for react-select based on theme
  const selectStyles = theme === 'dark' ? {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#f59e0b' : '#475569',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: '#475569',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#78350f' : state.isSelected ? '#f59e0b' : '#1e293b',
      color: state.isSelected ? 'white' : '#e2e8f0',
    }),
    singleValue: (base) => ({ ...base, color: '#e2e8f0' }),
    input: (base) => ({ ...base, color: '#e2e8f0' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  } : {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#fef3c7' : state.isSelected ? '#fbbf24' : 'white',
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <ArrowLeftCircle size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Kthim ose Ndryshim Produkti
      </h2>

      <div className="card-premium p-6 mb-6 flex gap-4">
        <input
          type="text"
          value={kuponKodi}
          onChange={(e) => setKuponKodi(e.target.value)}
          placeholder="Fut kodin e kuponit"
          className="input-premium flex-1"
        />
        <button onClick={handleKerkoKupon} className="btn-primary px-6">
          Kërko
        </button>
      </div>

      {shitje && (
        <>
		
		 <div className="flex justify-end gap-4 mt-6 animate-slide-up">
            <button
              onClick={handleRikthim}
              className="btn-danger px-6 py-2.5 flex items-center gap-2"
            >
              <ArrowLeftCircle size={18} /> Rikthe Kuponin
            </button>
            <button
              onClick={handleNdryshim}
              className="btn-warning px-6 py-2.5 flex items-center gap-2"
            >
              <RefreshCcw size={18} /> Përditëso Kuponin
            </button>
          </div>
          
           {roli === 'admin' && (
        <div className="card-premium p-4 mb-6 max-w-sm">
          <label className="form-label">Lokacioni</label>
          <Select
            options={lokacionet.map(l => ({ value: l.id, label: l.emri }))}
            value={
        lokacionet.find(l => l.id === lokacioniShitjes)
          ? {
              value: lokacioniShitjes,
              label: lokacionet.find(l => l.id === lokacioniShitjes)?.emri
            }
          : null
      }
            isDisabled={true}
            placeholder="Zgjidh lokacionin"
            className="w-full"
            isClearable
            styles={selectStyles}
            classNamePrefix="react-select"
          />
        </div>
      )}
		  
          {/* forma për shtimin e produktit */}
          <div className="card-premium p-6 mb-6 grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
          <Select
  options={produktet
    .filter(p => p.eshte_aktiv != 2)
    .map(p => ({
      value: p.id,
      label: `${p.kodi || ''} - ${p.emri}`,
      image: p.foto?.[0]?.file_path ? `${env.API_URL_Image}/storage/${p.foto[0].file_path}` : null
    }))}
  value={
    item.produkt_id
      ? {
          value: item.produkt_id,
          label: `${produktet.find(p => p.id === parseInt(item.produkt_id))?.kodi || ''} - ${produktet.find(p => p.id === parseInt(item.produkt_id))?.emri}`,
          image: produktet.find(p => p.id === parseInt(item.produkt_id))?.foto?.[0]?.file_path
            ? `${env.API_URL_Image}/storage/${produktet.find(p => p.id === parseInt(item.produkt_id)).foto[0].file_path}`
            : null
        }
      : null
  }
  onChange={(option) => handleChange('produkt_id', option?.value || '')}
  placeholder="Zgjidh produktin"
  className="w-full"
  isClearable
  styles={selectStyles}
  classNamePrefix="react-select"
  formatOptionLabel={(option) => (
    <div className="flex items-center gap-2">
      {option.image && (
        <img src={option.image} alt={option.label} className="w-8 h-8 object-cover rounded" />
      )}
      <span>{option.label}</span>
    </div>
  )}
  filterOption={(option, inputValue) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  }
/>

 
 <Select
  options={madhesiUnike.map((m) => ({ value: m.id, label: m.emri }))}
 value={madhesiUnike
  .map(m => ({ value: m.id, label: m.emri }))
  .find(option => option.value === item.madhesi_id) || null}
  onChange={(option) => handleChange('madhesi_id', option?.value || '')}
  placeholder="Zgjidh madhësinë"
  className="w-full"
  isClearable
  styles={selectStyles}
  classNamePrefix="react-select"
  formatOptionLabel={(option) => (
    <div className="flex items-center gap-2">
      <span>{option.label}</span>
    </div>
  )}
/>

       <Select
  options={ngjyraUnike.map((n) => ({ value: n.id, label: n.emri }))}
 value={ngjyraUnike
  .map(n => ({ value: n.id, label: n.emri }))
  .find(option => option.value === item.ngjyra_id) || null}
  onChange={(option) => handleChange('ngjyra_id', option?.value || '')}
  placeholder="Zgjidh ngjyrën"
  className="w-full"
  isClearable
  styles={selectStyles}
  classNamePrefix="react-select"
  formatOptionLabel={(option) => (
    <div className="flex items-center gap-2">
      <span>{option.label}</span>
    </div>
  )}
/>

        <input type="number" min="1" max={getMaxSasi()} value={item.sasia} onChange={(e) => handleChange('sasia', e.target.value)} className="input-premium" />
        <div className="text-sm text-gray-600 dark:text-gray-400">Max: {getMaxSasi()}</div>
       

<div className="flex flex-col items-end gap-1 text-right">
  <div className="flex items-center gap-2">
    <span className="text-sm text-gray-600 dark:text-gray-400">Çmimi:</span>
    <input
      type="number"
      step="1"
      className="input-premium w-24 text-right"
      value={Number(item.cmimi || 0).toFixed(2)}
      onChange={(e) => handleChange('cmimi', parseFloat(e.target.value))}
    />
  </div>

  {item.zbritje > 0 && (
    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
      <span className="line-through text-red-600 dark:text-red-400">{item.cmimi_origjinal} {currencyCode}</span>
      <span className="text-green-700 dark:text-green-400 font-semibold">-{item.zbritje}%</span>
    </div>
  )}

  <div className="text-sm text-gray-600 dark:text-gray-300">
    x {item.sasia} = <span className="font-bold text-primary-600 dark:text-primary-400">{(item.cmimi * item.sasia).toFixed(2)} {currencyCode}</span>
  </div>
</div>




        <button onClick={handleAdd} className="btn-premium-secondary flex items-center justify-center gap-2 w-full text-green-700 dark:text-green-400 border-green-700 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20">
          <PlusCircle size={16} /> Shto
        </button>
          </div>



                <div className="hidden md:block overflow-x-auto card-premium p-0 animate-fade-in">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>Produkti</th>
                  <th>Madhësia</th>
                  <th>Ngjyra</th>
                  <th>Çmimi</th>
                  <th>Sasia</th>
                  <th>Total</th>
                  <th>Fshi</th>
                </tr>
              </thead>
              <tbody>
                {(shitje?.detaje ?? []).map((d, idx) => (
                  <tr key={idx}>
                    <td>
                      {d.produkt?.foto?.[0]?.file_path && (
                        <img
                          src={`${env.API_URL_Image}/storage/${d.produkt.foto[0].file_path}`}
                          alt=""
                          className="w-12 h-12 object-cover rounded-lg shadow-sm"
                        />
                      )}
                    </td>
                    <td className="font-medium">{d.produkt.emri}</td>
                    <td>{d.madhesi.emri}</td>
                    <td>{d.ngjyra.emri}</td>
                    <td className="font-semibold text-primary-600 dark:text-primary-400">{d.cmimi_shitjes} {currencyCode}</td>
                    <td className="text-center">{d.sasia}</td>
                    <td className="font-bold text-lg text-primary-700 dark:text-primary-300">{(d.cmimi_shitjes * d.sasia).toFixed(2)} {currencyCode}</td>
                    <td>
                      <button onClick={() => handleRemove(idx)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors">
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
                {/* View për mobile – karta për çdo produkt të shtuar në shitje */}
<div className="block md:hidden space-y-4 mt-6 animate-fade-in">
  {(shitje?.detaje ?? []).map((s, idx) => (
    <div
      key={`${s.produkt_id}-${s.madhesi_id}-${s.ngjyra_id}`}
      className="card-premium p-4 relative animate-slide-up"
    >
      <button
        onClick={() => handleRemove(idx)}
        className="absolute top-3 right-3 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
      >
        <Trash2 size={18} />
      </button>

      <div className="flex items-center gap-3 mb-3">
       {s.produkt?.foto?.[0]?.file_path && (
  <img
    src={`${env.API_URL_Image}/storage/${s.produkt.foto[0].file_path}`}
    alt=""
    className="w-16 h-16 object-cover rounded-lg shadow-md"
  />
)}

        <div>
          <p className="font-semibold text-gray-800 dark:text-gray-100">
            {produktet.find(p => p.id === parseInt(s.produkt_id))?.emri}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Madhësia: {s.madhesi.emri} • Ngjyra: {s.ngjyra.emri}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-200">
        <div>
          <strong>Çmimi:</strong> <span className="text-primary-600 dark:text-primary-400 font-semibold">{s.cmimi_shitjes} {currencyCode}</span>
        </div>
        <div>
          <strong>Sasia:</strong> <span className="font-semibold">{s.sasia}</span>
        </div>
        <div className="col-span-2 font-bold text-lg text-right text-primary-700 dark:text-primary-300 mt-2">
          Total: {(s.cmimi_shitjes * s.sasia).toFixed(2)} {currencyCode}
        </div>
      </div>
    </div>
  ))}
</div>

          <div className="text-right text-2xl font-bold mt-8 text-primary-800 dark:text-primary-200 animate-slide-up">
            Totali: <span className="text-primary-600 dark:text-primary-400">{(Number(shitje?.totali) || 0).toFixed(2)} {currencyCode}</span>
          </div>

        </>
      )}
    </div>
  );
}

export default KthimProdukti;
